import { Loader2, Pencil, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import type { CertificateRequestIntentDto } from "@/services/certificateRequestIntentsService";
import {
  listPlatformCertificateRequestIntents,
  upsertCertificateRequestIntent,
  type UpsertCertificateRequestIntentBody,
} from "@/services/platformCertificateRequestIntentsService";

const QUERY_KEY = ["platform", "certificate-request-intents"] as const;

/** Matches backend `UpsertCertificateRequestIntentDto` slug pattern. */
const INTENT_SLUG_REGEX = /^[a-z][a-z0-9_]{0,62}$/;

type IntentDialogMode = "add" | "edit";

type IntentDialogForm = {
  slug: string;
  status: UpsertCertificateRequestIntentBody["status"];
  flowKey: UpsertCertificateRequestIntentBody["flowKey"];
  sortOrder: string;
};

function defaultFormForAdd(nextSortOrder: number): IntentDialogForm {
  return {
    slug: "",
    status: "coming_soon",
    flowKey: "open_company",
    sortOrder: String(nextSortOrder),
  };
}

export function CertificateRequestIntentsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listPlatformCertificateRequestIntents,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<IntentDialogMode | null>(null);
  const [form, setForm] = useState<IntentDialogForm>(defaultFormForAdd(0));

  const rows = listQuery.data ?? [];

  useEffect(() => {
    pagination.setTotal(rows.length);
  }, [rows.length, pagination]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return rows.slice(start, start + pagination.limit);
  }, [rows, pagination.page, pagination.limit]);

  const saveMutation = useMutation({
    mutationFn: upsertCertificateRequestIntent,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t("platform.certificateRequestIntents.saveSuccess"));
      setDialogOpen(false);
      setDialogMode(null);
    },
    onError: () => {
      toast.error(t("platform.certificateRequestIntents.saveError"));
    },
  });

  const openAdd = useCallback((): void => {
    const nextSort =
      rows.length > 0 ? Math.max(...rows.map((r) => r.sortOrder ?? 0)) + 1 : 0;
    setForm(defaultFormForAdd(nextSort));
    setDialogMode("add");
    setDialogOpen(true);
  }, [rows]);

  const openEdit = useCallback((row: CertificateRequestIntentDto): void => {
    setForm({
      slug: row.slug,
      status: row.status as UpsertCertificateRequestIntentBody["status"],
      flowKey: row.flowKey as UpsertCertificateRequestIntentBody["flowKey"],
      sortOrder: String(row.sortOrder ?? 0),
    });
    setDialogMode("edit");
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback((): void => {
    if (saveMutation.isPending) return;
    setDialogOpen(false);
    setDialogMode(null);
  }, [saveMutation.isPending]);

  const handleSave = useCallback((): void => {
    if (dialogMode == null) return;
    const parsed = parseInt(form.sortOrder.replace(/\s/g, ""), 10);
    const sortOrder = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    const slug =
      dialogMode === "add" ? form.slug.trim().toLowerCase() : form.slug.trim();
    if (dialogMode === "add") {
      if (!INTENT_SLUG_REGEX.test(slug)) {
        toast.error(t("platform.certificateRequestIntents.invalidSlug"));
        return;
      }
    }
    saveMutation.mutate({
      slug,
      status: form.status,
      flowKey: form.flowKey,
      sortOrder,
    });
  }, [dialogMode, form.flowKey, form.slug, form.sortOrder, form.status, saveMutation, t]);

  const handleRefresh = useCallback((): void => {
    void listQuery.refetch();
  }, [listQuery]);

  const statusLabel = useCallback(
    (status: string): string => {
      if (status === "active") return t("platform.certificateRequestIntents.statusActive");
      if (status === "coming_soon") return t("platform.certificateRequestIntents.statusComingSoon");
      if (status === "hidden") return t("platform.certificateRequestIntents.statusHidden");
      return status;
    },
    [t],
  );

  const flowLabel = useCallback(
    (flowKey: string): string => {
      if (flowKey === "certificate_catalog") {
        return t("platform.certificateRequestIntents.flowCertificateCatalog");
      }
      if (flowKey === "open_company") {
        return t("platform.certificateRequestIntents.flowOpenCompany");
      }
      if (flowKey === "enterprise_catalog") {
        return t("platform.certificateRequestIntents.flowEnterpriseCatalog");
      }
      if (flowKey === "quote_request") {
        return t("platform.certificateRequestIntents.flowQuoteRequest");
      }
      return flowKey;
    },
    [t],
  );

  if (listQuery.isPending) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader
          title={t("platform.certificateRequestIntents.title")}
          description={t("platform.certificateRequestIntents.description")}
        />
        <TableCard className="mt-4">
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        </TableCard>
      </div>
    );
  }

  if (listQuery.isError) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t("platform.certificateRequestIntents.title")} />
        <TableCard className="mt-4">
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6 py-12">
            <EmptyState
              title={t("platform.certificateRequestIntents.loadError")}
              action={
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void listQuery.refetch()}
                  className="transition-colors duration-150"
                >
                  {t("platform.certificateRequestIntents.retryLoad")}
                </Button>
              }
            />
          </div>
        </TableCard>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("platform.certificateRequestIntents.title")}
        description={t("platform.certificateRequestIntents.description")}
      />

      <div className="mt-4 flex flex-col items-end gap-2">
        <Button
          type="button"
          onClick={openAdd}
          className="transition-colors duration-150"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("platform.certificateRequestIntents.addIntent")}
        </Button>
      </div>

      <TableCard className="mt-4">
        {rows.length === 0 ? (
          <div className="flex min-h-[40vh] flex-col items-center justify-center px-6 py-12">
            <EmptyState
              title={t("platform.certificateRequestIntents.empty")}
              action={
                <Button
                  type="button"
                  onClick={openAdd}
                  className="transition-colors duration-150"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("platform.certificateRequestIntents.addIntent")}
                </Button>
              }
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("platform.certificateRequestIntents.colSlug")}</TableHead>
                    <TableHead>{t("platform.certificateRequestIntents.colStatus")}</TableHead>
                    <TableHead>{t("platform.certificateRequestIntents.colFlowKey")}</TableHead>
                    <TableHead>{t("platform.certificateRequestIntents.colSortOrder")}</TableHead>
                    <TableHead className="text-right">
                      {t("platform.certificateRequestIntents.colActions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRows.map((row) => (
                    <TableRow key={row.slug}>
                      <TableCell className="font-medium text-foreground">{row.slug}</TableCell>
                      <TableCell>{statusLabel(row.status)}</TableCell>
                      <TableCell>{flowLabel(row.flowKey)}</TableCell>
                      <TableCell>{row.sortOrder}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(row)}
                          className="transition-colors duration-150"
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          {t("platform.certificateRequestIntents.edit")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("platform.certificateRequestIntents.rowsPerPage")}
              pageOfLabel={t("platform.certificateRequestIntents.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              })}
              onRefresh={handleRefresh}
              isRefreshing={listQuery.isFetching}
            />
          </>
        )}
      </TableCard>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === "add"
                ? t("platform.certificateRequestIntents.addDialogTitle")
                : t("platform.certificateRequestIntents.dialogTitle")}
            </DialogTitle>
          </DialogHeader>
          {dialogMode != null ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cri-slug">{t("platform.certificateRequestIntents.slugLabel")}</Label>
                <Input
                  id="cri-slug"
                  value={form.slug}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))
                  }
                  disabled={dialogMode === "edit"}
                  readOnly={dialogMode === "edit"}
                  placeholder={t("platform.certificateRequestIntents.slugPlaceholder")}
                  className={dialogMode === "edit" ? "bg-muted/50" : undefined}
                />
                <p className="text-xs text-muted-foreground">
                  {t("platform.certificateRequestIntents.slugHint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{t("platform.certificateRequestIntents.statusLabel")}</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      status: v as UpsertCertificateRequestIntentBody["status"],
                    }))
                  }
                >
                  <SelectTrigger className="transition-colors duration-150">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("platform.certificateRequestIntents.statusActive")}</SelectItem>
                    <SelectItem value="coming_soon">
                      {t("platform.certificateRequestIntents.statusComingSoon")}
                    </SelectItem>
                    <SelectItem value="hidden">{t("platform.certificateRequestIntents.statusHidden")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("platform.certificateRequestIntents.flowKeyLabel")}</Label>
                <Select
                  value={form.flowKey}
                  onValueChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      flowKey: v as UpsertCertificateRequestIntentBody["flowKey"],
                    }))
                  }
                >
                  <SelectTrigger className="transition-colors duration-150">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="certificate_catalog">
                      {t("platform.certificateRequestIntents.flowCertificateCatalog")}
                    </SelectItem>
                    <SelectItem value="open_company">
                      {t("platform.certificateRequestIntents.flowOpenCompany")}
                    </SelectItem>
                    <SelectItem value="enterprise_catalog">
                      {t("platform.certificateRequestIntents.flowEnterpriseCatalog")}
                    </SelectItem>
                    <SelectItem value="quote_request">
                      {t("platform.certificateRequestIntents.flowQuoteRequest")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t("platform.certificateRequestIntents.flowKeyHint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cri-sort">{t("platform.certificateRequestIntents.sortOrderLabel")}</Label>
                <Input
                  id="cri-sort"
                  inputMode="numeric"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                />
              </div>
            </div>
          ) : null}
          <Card className="border-border shadow-sm">
            <CardContent className="flex flex-row justify-between pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                disabled={saveMutation.isPending}
                className="transition-colors duration-150"
              >
                {t("platform.certificateRequestIntents.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saveMutation.isPending || dialogMode == null}
                className="transition-colors duration-150"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                    {t("platform.certificateRequestIntents.saving")}
                  </>
                ) : (
                  t("platform.certificateRequestIntents.save")
                )}
              </Button>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </div>
  );
}
